import './PartnerPage.scss';
import CircularProgress from '../../../../shared/components/CircularProgress/CircularProgress';
import React, { useEffect, useState } from 'react';
import Alert from '../../../../shared/components/Alert/Alert';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Box from '../../../../shared/components/Box/Box';
import ObjectTabs from '../../../../shared/components/ObjectTabs/ObjectTabs';
import PartnerAgenciesDialog from '../components/PartnerAgenciesDialog';
import PartnerAgenciesPanel from '../components/PartnerAgenciesPanel';
import PartnerCommercialPanel from '../components/PartnerCommercialPanel';
import PartnerContactsDialog from '../components/PartnerContactsDialog';
import PartnerContactsPanel from '../components/PartnerContactsPanel';
import PartnerEditDialog from '../components/PartnerEditDialog';
import PartnerHeader from '../components/PartnerHeader';
import PartnerInformationPanel from '../components/PartnerInformationPanel';
import PartnerQuoteSettingsPanel from '../components/PartnerQuoteSettingsPanel';
import PartnerSummaryBand from '../components/PartnerSummaryBand';
import {
  createPartnerActivity, getPartnerMarketingScore, updatePartner,
  updatePartnerAgencies, updatePartnerContacts, verifyPartner,
} from '../partner.api';
import {
  buildPartnerForm, normalizePartnerAgency, normalizePartnerContact, updatePartnerContactField,
} from '../partnerModel';
import {
  validatePartner, validatePartnerAgency, validatePartnerContact,
} from '../partnerValidation';
import usePartnerDetails from '../../../../hooks/usePartnerDetails';

const PARTNER_TABS = [
  { key: 'information', label: 'Vue d’ensemble' }, { key: 'activities', label: 'Activités' },
  { key: 'affairs', label: 'Affaires' }, { key: 'contacts', label: 'Contacts' },
  { key: 'documents', label: 'Documents' }, { key: 'agencies', label: 'Agences', secondary: true },
  { key: 'projects', label: 'Projets', secondary: true }, { key: 'quotes', label: 'Devis', secondary: true },
  { key: 'metrics', label: 'Indicateurs', secondary: true }, { key: 'quote-settings', label: 'Conditions de devis', secondary: true },
];
const EMPTY_CONTACT = normalizePartnerContact({});
const EMPTY_AGENCY = normalizePartnerAgency({});
const hasValue = (item) => Object.entries(item).some(([key, value]) => !['id', 'countryCode', 'agencyId', 'agencyClientKey', 'clientKey', '_contactNameEdited'].includes(key) && String(value || '').trim());
const validationMessage = (validation) => Object.values(validation.fieldErrors).join(' ');

function PartnerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { partner, setPartner, agencies, setAgencies, overview, loading, error, setError,
    agenciesLoading, agenciesError, overviewLoading, overviewError, loadPartner, loadOverview } = usePartnerDetails(id);
  const [selectedAgency, setSelectedAgency] = useState(null);
  useEffect(() => setSelectedAgency(null), [id]);
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'information');
  useEffect(() => { const requested = searchParams.get('tab'); if (requested) setActiveTab(requested); }, [searchParams]);
  const [dialog, setDialog] = useState(null);
  const [partnerForm, setPartnerForm] = useState(buildPartnerForm({}));
  const [contactsForm, setContactsForm] = useState([]);
  const [agenciesForm, setAgenciesForm] = useState([]);
  const [modalError, setModalError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [marketingScore, setMarketingScore] = useState(null);

  useEffect(() => {
    let active = true;
    if (partner?.relationshipStatus !== 'prospect') {
      setMarketingScore(null);
      return () => { active = false; };
    }
    getPartnerMarketingScore(partner.id)
      .then((score) => { if (active) setMarketingScore(score); })
      .catch(() => { if (active) setMarketingScore(null); });
    return () => { active = false; };
  }, [partner?.id, partner?.relationshipStatus]);

  const openDialog = (name) => {
    setModalError(null); setDialog(name);
    if (name === 'partner') setPartnerForm({ ...buildPartnerForm(partner), type: partner.type || '' });
    if (name === 'contacts') setContactsForm(partner.contacts.length ? partner.contacts.map(normalizePartnerContact) : [{ ...EMPTY_CONTACT }]);
    if (name === 'agencies') setAgenciesForm(agencies.map(normalizePartnerAgency));
  };
  const closeDialog = () => { if (!saving) { setDialog(null); setModalError(null); } };
  const changePartner = ({ target: { name, value } }) => setPartnerForm((current) => ({ ...current, [name]: name === 'countryCode' ? value.toUpperCase().slice(0, 2) : value }));
  const changeRow = (setter) => (index, { target: { name, value } }) => setter((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [name]: name === 'countryCode' ? value.toUpperCase().slice(0, 2) : value } : row));
  const changeContact = (index, { target: { name, value } }) => setContactsForm((current) => current.map((contact, contactIndex) => (
    contactIndex === index ? updatePartnerContactField(contact, name, value) : contact
  )));

  const savePartner = async () => {
    const validation = validatePartner(partnerForm); if (!validation.isValid) { setModalError(validationMessage(validation)); return; }
    try { setSaving(true); const updated = await updatePartner(id, partnerForm); setPartner(updated); setDialog(null); }
    catch (requestError) { setModalError(requestError.message); } finally { setSaving(false); }
  };
  const saveContacts = async () => {
    const rows = contactsForm.filter(hasValue); const invalid = rows.map(validatePartnerContact).find((item) => !item.isValid);
    if (invalid) { setModalError(validationMessage(invalid)); return; }
    try { setSaving(true); const updated = await updatePartnerContacts(id, rows); setPartner(updated); setDialog(null); }
    catch (requestError) { setModalError(requestError.message); } finally { setSaving(false); }
  };
  const saveAgencies = async () => {
    const rows = agenciesForm.filter(hasValue).map((agency) => ({ ...agency, socialReason: agency.socialReason || agency.name })); const invalid = rows.map(validatePartnerAgency).find((item) => !item.isValid);
    if (invalid) { setModalError(validationMessage(invalid)); return; }
    try { setSaving(true); const updated = await updatePartnerAgencies(id, rows); setPartner(updated); setAgencies(updated.agencies); setSelectedAgency(null); setDialog(null); }
    catch (requestError) { setModalError(requestError.message); } finally { setSaving(false); }
  };
  const verify = async () => { try { setVerifying(true); setPartner(await verifyPartner(id)); } catch (requestError) { setError(requestError.message); } finally { setVerifying(false); } };
  const addActivity = async (payload) => {
    try {
      setActivitySaving(true); setActivityError(null);
      await createPartnerActivity(id, payload);
      await Promise.all([loadPartner(), loadOverview()]);
      if (partner.relationshipStatus === 'prospect') setMarketingScore(await getPartnerMarketingScore(id));
    } catch (requestError) { setActivityError(requestError.message || 'Erreur lors de l’ajout de l’activité'); throw requestError; }
    finally { setActivitySaving(false); }
  };

  const panel = partner && (activeTab === 'information' ? <PartnerInformationPanel partner={partner} marketingScore={marketingScore} /> : activeTab === 'contacts' ? <PartnerContactsPanel contacts={partner.contacts} agencies={agencies} /> : activeTab === 'agencies' ? <PartnerAgenciesPanel agencies={agencies} loading={agenciesLoading} error={agenciesError} selectedAgency={selectedAgency} onSelect={setSelectedAgency} /> : activeTab === 'quote-settings' ? <PartnerQuoteSettingsPanel partner={partner} /> : <PartnerCommercialPanel activeTab={activeTab} loading={overviewLoading} overview={overview} onNavigate={navigate} contacts={partner.contacts} relationshipStatus={partner.relationshipStatus} activityError={activityError} activitySaving={activitySaving} onAddActivity={addActivity} />);

  return <Box className="page-container partner-page">
    <div className="partner-page__back"><button className="basic-button" onClick={() => navigate(-1)} type="button">Retour</button></div>
    {loading && <div className="partner-loading"><CircularProgress /></div>}{error && <Alert severity="error">{error}</Alert>}
    {partner && <><PartnerHeader partner={partner} verifying={verifying} onAddAction={() => setActiveTab('activities')} onCreateAffair={() => navigate(`/affaires?companyId=${partner.id}`)} onEditPartner={() => openDialog('partner')} onEditContacts={() => openDialog('contacts')} onEditAgencies={() => openDialog('agencies')} onVerify={verify} />
      <PartnerSummaryBand overview={overview} loading={overviewLoading} error={overviewError} /><ObjectTabs activeTab={activeTab} ariaLabel="Navigation du partenaire" onChange={setActiveTab} tabs={PARTNER_TABS} />{panel}
      <PartnerEditDialog open={dialog === 'partner'} form={partnerForm} error={modalError} saving={saving} onChange={changePartner} onClose={closeDialog} onSubmit={savePartner} />
      <PartnerContactsDialog open={dialog === 'contacts'} contacts={contactsForm} agencies={agencies} error={modalError} saving={saving} onChange={changeContact} onAdd={() => setContactsForm((current) => [...current, { ...EMPTY_CONTACT }])} onRemove={(index) => setContactsForm((current) => current.filter((_, rowIndex) => rowIndex !== index))} onClose={closeDialog} onSubmit={saveContacts} />
      <PartnerAgenciesDialog open={dialog === 'agencies'} agencies={agenciesForm} error={modalError} saving={saving} onChange={changeRow(setAgenciesForm)} onAdd={() => setAgenciesForm((current) => [...current, { ...EMPTY_AGENCY }])} onRemove={(index) => setAgenciesForm((current) => current.filter((_, rowIndex) => rowIndex !== index))} onClose={closeDialog} onSubmit={saveAgencies} />
    </>}
  </Box>;
}

export default PartnerPage;
